import { ref, reactive, computed } from 'vue'
import { useChatSocket } from './useChatSocket'

/**
 * Friends + friend-requests reactive state.
 *
 * Source of truth: REST (`/api/chat/friends` + `/api/chat/friends/requests`)
 * on load; WS frames (`friend.request`, `friend.accept`, `friend.remove`)
 * keep it live. `friend.pendingCount` is used for the tab badge and is
 * recomputed from the incoming list so we don't fight the server counter.
 */

export interface FriendUser { id: string; name: string; avatar: string | null; role: string; openForCollabs?: boolean }

export interface FriendRow {
    id: string
    user: FriendUser
    status: 'pending' | 'accepted' | 'blocked'
    direction: 'incoming' | 'outgoing'
    createdAt: string
    acceptedAt: string | null
}

const friends = ref<FriendRow[]>([])
const incoming = ref<FriendRow[]>([])
const outgoing = ref<FriendRow[]>([])
const loading = ref(false)
const loaded = ref(false)
const actionInFlight = reactive(new Set<string>())

let wired = false

const incomingCount = computed(() => incoming.value.length)

function upsert(list: any, row: FriendRow) {
    const idx = list.value.findIndex((r: FriendRow) => r.id === row.id)
    if (idx >= 0) list.value[idx] = row
    else list.value.unshift(row)
}

function removeById(list: any, id: string) {
    list.value = list.value.filter((r: FriendRow) => r.id !== id)
}

async function loadAll(): Promise<void> {
    loading.value = true
    try {
        const [friendsRes, reqsRes] = await Promise.all([
            $fetch<{ friends: FriendRow[] }>('/api/chat/friends'),
            $fetch<{ incoming: FriendRow[]; outgoing: FriendRow[] }>('/api/chat/friends/requests'),
        ])
        friends.value = friendsRes.friends || []
        incoming.value = reqsRes.incoming || []
        outgoing.value = reqsRes.outgoing || []
        loaded.value = true
    } catch (err: any) {
        if (err?.statusCode === 404) loaded.value = true
        else console.error('[chat:friends] load failed', err)
    } finally {
        loading.value = false
    }
}

function wire() {
    if (wired) return
    wired = true
    const { on } = useChatSocket()

    on('friend.request', (frame) => {
        if (!frame?.from?.id || !frame?.friendshipId) return
        const row: FriendRow = {
            id: String(frame.friendshipId),
            user: frame.from,
            status: 'pending',
            direction: 'incoming',
            createdAt: new Date().toISOString(),
            acceptedAt: null
        }
        upsert(incoming, row)
    })

    on('friend.accept', (frame) => {
        if (!frame?.by?.id || !frame?.friendshipId) return
        const id = String(frame.friendshipId)
        // Remove from incoming/outgoing if it was there
        removeById(incoming, id)
        removeById(outgoing, id)
        // Add to accepted friends
        const row: FriendRow = {
            id,
            user: frame.by,
            status: 'accepted',
            direction: 'outgoing', // doesn't really matter post-accept
            createdAt: new Date().toISOString(),
            acceptedAt: new Date().toISOString()
        }
        upsert(friends, row)
    })

    on('friend.remove', (frame) => {
        const id = frame?.friendshipId ? String(frame.friendshipId) : null
        if (!id) return
        removeById(friends, id)
        removeById(incoming, id)
        removeById(outgoing, id)
    })

    // friend.pendingCount is informational — the incoming list drives the badge.
    // If count drifts from our list length, reload to reconcile.
    on('friend.pendingCount', (frame) => {
        const n = Number(frame?.count) || 0
        if (loaded.value && n !== incoming.value.length) {
            loadAll().catch(() => { /* noop */ })
        }
    })

    on('__open', () => { if (loaded.value) loadAll() })

    // Public-profile patch from any tenant peer — right now this only
    // carries `openForCollabs`. Update any matching row in friends +
    // incoming + outgoing so the UI reflects the flag flip without
    // waiting for a full reload. No-op if the peer isn't in our rolodex.
    on('profile.update', (frame) => {
        if (!frame?.userId || !frame?.patch) return
        const patch = frame.patch
        const apply = (list: any) => {
            const idx = list.value.findIndex((r: FriendRow) => r.user?.id === frame.userId)
            if (idx < 0) return
            const row = list.value[idx]
            // Replace the row + user object so Vue reactivity fires
            // (shallow ref comparison on the row + deep field swap on the
            // user). Otherwise the badge wouldn't re-render because the
            // existing user object would be the same reference.
            list.value[idx] = { ...row, user: { ...row.user, ...patch } }
        }
        apply(friends)
        apply(incoming)
        apply(outgoing)
    })
}

/* ───────────────── Actions ───────────────── */

/**
 * Send a friend request. Accepts either a resolved userId (from the name
 * search dropdown) OR a short chatId (from the "Add by Chat ID" input). The
 * server dedupes both paths through the same Friendship row.
 */
async function sendRequest(
    target: string,
    opts: { byChatId?: boolean } = {}
): Promise<{ ok: boolean; autoAccepted: boolean; friendship?: FriendRow; error?: string }> {
    const flightKey = opts.byChatId ? `req:chat:${target}` : `req:${target}`
    if (actionInFlight.has(flightKey)) return { ok: false, autoAccepted: false, error: 'in_flight' }
    actionInFlight.add(flightKey)
    try {
        const body = opts.byChatId ? { chatId: target } : { toUserId: target }
        const res = await $fetch<{ friendship: FriendRow; autoAccepted: boolean }>('/api/chat/friends/request', {
            method: 'POST',
            body
        })
        if (res.autoAccepted) {
            removeById(incoming, res.friendship.id)
            removeById(outgoing, res.friendship.id)
            upsert(friends, { ...res.friendship, status: 'accepted' })
        } else if (res.friendship.status === 'pending') {
            upsert(outgoing, { ...res.friendship, direction: 'outgoing' })
        } else if (res.friendship.status === 'accepted') {
            upsert(friends, res.friendship)
        }
        return { ok: true, autoAccepted: res.autoAccepted, friendship: res.friendship }
    } catch (err: any) {
        return { ok: false, autoAccepted: false, error: err?.data?.statusMessage || err?.message || 'request_failed' }
    } finally {
        actionInFlight.delete(flightKey)
    }
}

async function accept(friendshipId: string): Promise<boolean> {
    if (actionInFlight.has(friendshipId)) return false
    actionInFlight.add(friendshipId)
    try {
        const res = await $fetch<{ friendship: FriendRow }>(`/api/chat/friends/${friendshipId}/accept`, { method: 'POST' })
        removeById(incoming, friendshipId)
        upsert(friends, res.friendship)
        return true
    } catch (err) {
        console.error('[chat:friends] accept failed', err)
        return false
    } finally {
        actionInFlight.delete(friendshipId)
    }
}

async function reject(friendshipId: string): Promise<boolean> {
    if (actionInFlight.has(friendshipId)) return false
    actionInFlight.add(friendshipId)
    try {
        await $fetch(`/api/chat/friends/${friendshipId}/reject`, { method: 'POST' })
        removeById(incoming, friendshipId)
        return true
    } catch (err) {
        console.error('[chat:friends] reject failed', err)
        return false
    } finally {
        actionInFlight.delete(friendshipId)
    }
}

async function removeFriend(friendshipId: string): Promise<boolean> {
    if (actionInFlight.has(friendshipId)) return false
    actionInFlight.add(friendshipId)
    try {
        await $fetch(`/api/chat/friends/${friendshipId}`, { method: 'DELETE' })
        removeById(friends, friendshipId)
        removeById(incoming, friendshipId)
        removeById(outgoing, friendshipId)
        return true
    } catch (err) {
        console.error('[chat:friends] remove failed', err)
        return false
    } finally {
        actionInFlight.delete(friendshipId)
    }
}

async function block(friendshipId: string): Promise<boolean> {
    if (actionInFlight.has(friendshipId)) return false
    actionInFlight.add(friendshipId)
    try {
        await $fetch(`/api/chat/friends/${friendshipId}/block`, { method: 'POST' })
        removeById(friends, friendshipId)
        removeById(incoming, friendshipId)
        removeById(outgoing, friendshipId)
        return true
    } catch (err) {
        console.error('[chat:friends] block failed', err)
        return false
    } finally {
        actionInFlight.delete(friendshipId)
    }
}

export function useFriends() {
    if (import.meta.client) {
        wire()
        if (!loaded.value) loadAll()
    }

    return {
        friends: computed(() => friends.value),
        incoming: computed(() => incoming.value),
        outgoing: computed(() => outgoing.value),
        incomingCount,
        loading: computed(() => loading.value),
        isActionInFlight: (id: string) => actionInFlight.has(id),
        reload: loadAll,
        sendRequest,
        accept,
        reject,
        removeFriend,
        block,
    }
}
